import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import { Expand } from "@sparefoot/react-commons";

import { unitTypesToIcon } from "config/unitIcons";
import { createBooking } from "store/modules/booking";
import { getLocalNumber } from "store/modules/phone";
import { Button } from "components/core/Button";
import { Paragraph, Text, ListItem } from "components/core/Typography";
import { UnitReservationDrawer } from "components/facility/UnitReservationDrawer";
import { UnitReservationForm } from "components/facility/UnitReservationForm";
import { ModalReservation } from "components/facility/ModalReservation";
import formatDate from "utils/formatDateReservation";
import formatUSD from "utils/formatUSD";
import { setCookie } from "utils/localStorageUtil";

import "./UnitsList.scss";

import { TrackingClient } from "@sparefoot/tracking-client";
import FeeTooltip from "../FeeTooltip/FeeTooltip";


const PricingBlock = ({ lowestPrice, startingPrice, quantity }) => {
	const showAvailabilityWarning = quantity && quantity <= 3;
	const showStartingPrice = startingPrice && lowestPrice < startingPrice;
	return (
		<div className="pricing-block">
			{showAvailabilityWarning && (
				<Paragraph
					align="center"
					size="small"
					color="orange"
					className="availability"
				>
					Only{" "}
					<Text weight="bold">
						{`${quantity} ${quantity > 1 ? "units" : "unit"}`}
					</Text>{" "}
					left
				</Paragraph>
			)}

			<Paragraph
				last
				weight="bold"
				align="right"
				className="special-type"
			>
				Web Only {showStartingPrice ? "Special" : "Rate"}
			</Paragraph>

			<div className="unit-price">
				<Paragraph
					nowrap
					last
					className="special-price"
				>
					<Text
						last
						color="green"
						weight="bold"
						size="medium"
					>
						{formatUSD(lowestPrice, { showDecimal: true })}
					</Text>{" "}
					<Text
						weight="medium"
						last
						size="small"
					>
						per month
					</Text>
				</Paragraph>

				{showStartingPrice && (
					<Text
						last
						color="gray"
						size="small"
						strikethrough
						className="starting-price"
					>
						{formatUSD(startingPrice)}
					</Text>
				)}
			</div>
		</div>
	);
};

PricingBlock.propTypes = {
	quantity: PropTypes.number,
	lowestPrice: PropTypes.number,
	startingPrice: PropTypes.number
};

// CARD TOP =================================================

const UnitCardTop = ({
	unitType,
	sizeText,
	typeText,
	special,
	availableAmenities,
	tierName,
	tierDescription
}) => {
	const entry = unitTypesToIcon[unitType] || unitTypesToIcon.storage;

	return (
		<div className="card-top">
			<div className="card-header">
				<div className="unit-icon">
					{React.cloneElement(entry, {
						size: "xlarge"
					})}
				</div>
				<div className="card-title">
					<Paragraph
						last
						weight="bold"
					>
						{tierName
							? `${sizeText} ${tierName} ${typeText}`
							: `${sizeText} ${typeText}`}
					</Paragraph>

					{special && (
						<Paragraph
							last
							color="gray"
							size="small"
						>
							{special}
						</Paragraph>
					)}
				</div>
			</div>
			<div className="card-description">
				{tierDescription && (
					<Paragraph
						color="gray"
						size="small"
					>
						{tierDescription}
					</Paragraph>
				)}
				<ul className="amenities">
					{availableAmenities.map((amenity) => (
						<ListItem
							key={amenity}
							size="small"
						>
							{amenity}
						</ListItem>
					))}
				</ul>
			</div>
		</div>
	);
};

UnitCardTop.propTypes = {
	unitType: PropTypes.string,
	sizeText: PropTypes.string,
	typeText: PropTypes.string,
	special: PropTypes.string,
	availableAmenities: PropTypes.arrayOf(PropTypes.string),
	tierName: PropTypes.string,
	tierDescription: PropTypes.string
};

// CARD BOTTOM =================================================

const UnitCardBottom = ({
	lowestPrice,
	startingPrice,
	quantity,
	drawerExpanded,
	handleToggleDrawer,
	unitId,
	totalFees,
	fees,
	showFeeWarning
}) => (
	<div className="card-bottom">
		<PricingBlock
			lowestPrice={lowestPrice}
			startingPrice={startingPrice}
			quantity={quantity}
		/>
		{showFeeWarning && totalFees > 0 && (
			<FeeTooltip
				fees={fees}
				totalFees={totalFees}
			/>
		)}
		<Button
			theme={drawerExpanded ? "tertiary" : "primary"}
			onClick={() => handleToggleDrawer(unitId)}
		>
			{drawerExpanded ? "Close" : "Reserve Now"}
		</Button>
	</div>
);
UnitCardBottom.propTypes = {
	lowestPrice: PropTypes.number,
	startingPrice: PropTypes.number,
	quantity: PropTypes.number,
	drawerExpanded: PropTypes.bool,
	handleToggleDrawer: PropTypes.func,
	unitId: PropTypes.number,
	totalFees: PropTypes.number,
	fees: PropTypes.arrayOf(PropTypes.object),
	showFeeWarning: PropTypes.bool
};

// UNIT CARD =================================================

const UnitCard = ({
	id,
	lowestPrice,
	startingPrice,
	quantity,
	unitType,
	typeText,
	sizeText,
	availableAmenities,
	special,
	handleToggleDrawer,
	drawerExpanded,
	reservationWindow,
	closures,
	facilityId,
	nearbyFacilitiesUrl,
	tierName,
	tierDescription,
	adminFee,
	totalFees,
	showFeeWarning,
	lockFee,
	cityUrl
}) => {
	const [showModal, setShowModal] = useState(false);
	const [isLoadingModal, setIsLoadingModal] = useState(false);
	const [successReservation, setSuccessReservation] = useState(false);
	const [errorReservationMessages, setErrorReservationMessages] =
		useState(undefined);

	const fees = [
		{
			name: "One-Time Admin Fee",
			price: adminFee
		},
		{
			name: "One-Time Lock Fee",
			price: lockFee
		}
	];

	const dispatch = useDispatch();
	const handleCreateBooking = async (booking) =>
		dispatch(createBooking(booking));
	const handleFetchNumber = async (city, state) => {
		const data = { path: `${city}/${state}` };
		const {
			payload: { result }
		} = await dispatch(getLocalNumber(data));
		return result;
	};

	const onClose = () => setShowModal(false);
	const otherFacilities = () => {
		setShowModal(false);
		window.location.href = nearbyFacilitiesUrl;
	};

	const onSubmit = async (data) => {
		const { alternateNames, moveInDate, ...restData } = data;
		const reservationData = {
			alternateFirstNames:
				alternateNames?.length > 0 &&
				alternateNames.map((name) => name.firstName),
			alternateLastNames:
				alternateNames?.length > 0 &&
				alternateNames.map((name) => name.lastName),
			moveInDate: formatDate(moveInDate),
			facilityId,
			unitId: id,
			...restData
		};

		setShowModal(true);
		setIsLoadingModal(true);

		try {
			// Track before creating booking
			TrackingClient.trackReservationStarted({
				cart_products_count: 1,
				cart_value: lowestPrice,
				currency: "usd",
				products: reservationData.unitId,
				unit: {
					currency: "usd",
					id: reservationData.unitId,
					variant_size: sizeText,
					price: lowestPrice,
					quantity: 1
				},
				unit_id: id,
				unit_promo: special
			});

			const reservationRequest = {
				method: "POST",
				body: reservationData
			};

			const reservationDone = await handleCreateBooking(
				reservationRequest
			);
			const { result } = reservationDone.payload;
			const { email } = reservationDone.payload.entities.bookings[result];
			const { bookingAuth } =
				reservationDone.payload.entities.bookings[result].rawBooking;

			if (bookingAuth) {
				setCookie("jwt", bookingAuth);
			}

			if (result && result !== "errorBooking") {
				// Track reservation completed
				TrackingClient.trackReservationCompleted({
					currency: "usd",
					discounts: "",
					first_order_date: Date.now(),
					last_order_date: Date.now(),
					move_in_date: formatDate(moveInDate),
					promotion_code: special,
					reservation_confirmation: result,
					revenue: lowestPrice,
					tax: "",
					unit: {
						currency: "usd",
						id: reservationData.unitId,
						variant_size: sizeText,
						price: lowestPrice,
						quantity: 1
					},
					unit_id: id,
					unit_promo: special,
					email: reservationData.email,
					first_name: reservationData.firstName,
					last_name: reservationData.lastName,
					phone: reservationData.phone
				});

				setSuccessReservation(true);
				if (typeof window !== "undefined") {
					// Client-side (browser) environment
					window.location.href = `/reservation/confirmation/${result}/?email=${email}`;
				}
			} else {
				const errorMessage =
					reservationDone.payload.entities.bookings.result.message;
				setErrorReservationMessages(errorMessage);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
		} finally {
			setIsLoadingModal(false);
		}
	};

	return (
		<div>
			{showModal && (
				<ModalReservation
					successReservation={successReservation}
					isLoadingModal={isLoadingModal}
					onClose={onClose}
					otherFacilities={otherFacilities}
					errorReservationMessages={errorReservationMessages}
				/>
			)}
			<div className="unit-card">
				{/* Main Card Body */}
				<div className="card-main">
					{/* CARD TOP */}
					<UnitCardTop
						unitType={unitType}
						sizeText={sizeText}
						typeText={typeText}
						special={special}
						availableAmenities={availableAmenities}
						tierName={tierName}
						tierDescription={tierDescription}
					/>
					{/* CARD BOTTOM */}
					<UnitCardBottom
						lowestPrice={lowestPrice}
						startingPrice={startingPrice}
						quantity={quantity}
						drawerExpanded={drawerExpanded}
						handleToggleDrawer={handleToggleDrawer}
						unitId={id}
						totalFees={totalFees}
						fees={fees}
						showFeeWarning={showFeeWarning}
					/>
				</div>
				{/* Card Drawer */}
				<Expand
					className="card-drawer"
					autoHeight
					expanded={drawerExpanded}
				>
					<div>
						{drawerExpanded && (
							<UnitReservationDrawer>
								<UnitReservationForm
									reservationWindow={reservationWindow}
									closures={closures}
									onSubmit={onSubmit}
									fees={fees}
									showFeeWarning={showFeeWarning}
									totalFees={totalFees}
									unitPrice={lowestPrice}
									cityUrl={cityUrl}
									errorReservationMessages={
										errorReservationMessages
									}
									setErrorReservationMessages={
										setErrorReservationMessages
									}
									handleFetchNumber={handleFetchNumber}
								/>
							</UnitReservationDrawer>
						)}
					</div>
				</Expand>
			</div>
		</div>
	);
};

UnitCard.propTypes = {
	id: PropTypes.number,
	special: PropTypes.string,
	sizeText: PropTypes.string,
	typeText: PropTypes.string,
	availableAmenities: PropTypes.arrayOf(PropTypes.string),
	quantity: PropTypes.number,
	lowestPrice: PropTypes.number,
	startingPrice: PropTypes.number,
	unitType: PropTypes.string,
	handleToggleDrawer: PropTypes.func,
	drawerExpanded: PropTypes.bool,
	reservationWindow: PropTypes.number,
	closures: PropTypes.arrayOf(PropTypes.string),
	facilityId: PropTypes.number,
	nearbyFacilitiesUrl: PropTypes.string,
	tierName: PropTypes.string,
	tierDescription: PropTypes.string,
	adminFee: PropTypes.number,
	totalFees: PropTypes.number,
	showFeeWarning: PropTypes.bool,
	lockFee: PropTypes.number,
	cityUrl: PropTypes.string
};

export default UnitCard;
